import { useTranslation } from 'react-i18next';
import { usePlots } from '../../../hooks';
import { Text, Group } from '@mantine/core';
import { Icon } from '../../../../lib/components';
import PlotsImportModal from '../../Plots/PlotsImportModal';
import NewTable from '../../../shared/components/NewTable/NewTable';
import {
  CellProps,
  CellValue,
} from '../../../shared/components/NewTable/useColumns';
import { Plot } from '../../../shared/types';
import { capitalize, truncate } from 'lodash';
import { WORLD_COUNTRY_LIST } from '../../../../assets/countryList';
import PlotsSidePanel from './PlotsSidePanel';
import useShowPlot from '../../../hooks/useShowPlot';
import AddPlots from './AddPlots';
import classes from './PlotsTablePanel.module.css';
import { m2ToHa, getPlotDeforestation, calculateDeforestation } from './utils';
import { SEARCH_PARAMS_KEYS } from '../../../router/searchParamsKeys';
import usePlotTableFilters from './usePlotTableFilters';

const DeforestationRisk = ({ row }: { row: Plot }) => {
  const { t } = useTranslation();
  const deforestation = calculateDeforestation(row.risk);

  const riskPercentage = deforestation
    ? deforestation?.percentage
    : t('common.status.processing');

  return (
    <Group gap="xs" miw={100}>
      {deforestation?.color && (
        <Icon icon="circle" width={8} color={deforestation?.color} />
      )}
      <Text c="white" size="sm" fw={400} span>
        {riskPercentage}
      </Text>
    </Group>
  );
};

export const PLOT_CARD_ROWS = [
  ['name', 'surfaceArea'],
  ['commodities', 'riskAssessment.status'],
  ['countryCode', 'suppliers.[0].name'],
];

export const renderPlotCardStatus = (plot: Plot) => {
  const deforestation = getPlotDeforestation(plot);

  return `8px solid ${deforestation?.color}`;
};

const NewPlotsTablePanel = () => {
  const { t } = useTranslation();

  const { data: plots, isLoading } = usePlots();
  const filters = usePlotTableFilters(plots);

  const showPlot = useShowPlot();

  const columns = [
    {
      className: classes.fixedWidth,
      cell: ({ row }: CellProps<Plot>) => (
        <Text title={row.name}>{truncate(row.name, { length: 50 })}</Text>
      ),
      cardCell: ({ row }: CellProps<Plot>) => (
        <Text size="lg" title={row.name}>
          {truncate(row.name, { length: 30 })}
        </Text>
      ),
      label: t('plots.table.name'),
      sort: true,
      value: 'name',
    },
    {
      renderText: (value: CellValue) =>
        truncate(String(value).split(',').map(capitalize).join(', '), {
          length: 50,
        }),
      label: t('plots.table.commodities'),
      sort: true,
      value: 'commodities',
    },
    {
      renderText: (value: CellValue) =>
        WORLD_COUNTRY_LIST?.find((country) => country.value === value)?.label ??
        String(value),
      label: t('map.sidePanel.details.properties.country'),
      sort: true,
      value: 'countryCode',
    },
    {
      label: t('common.roles.supplier'),
      sort: true,
      value: 'suppliers.[0].name',
    },
    {
      renderText: (value: CellValue) => m2ToHa(value),
      cardCell: ({ row }: CellProps<Plot>) => (
        <Text size="lg">{`${row.surfaceAreaHectares} ha`}</Text>
      ),
      label: `${t('plots.table.surfaceArea')} (ha)`,
      sort: true,
      value: 'surfaceArea',
    },
    {
      cell: NewTable.DateCell,
      label: t('plots.table.updatedAt'),
      sort: true,
      value: 'updatedAt',
    },

    {
      cell: DeforestationRisk,
      label: t('plots.table.risk'),
      sort: true,
      value: 'risk',
    },
    {
      value: 'riskAssessment.status',
      renderText: (value: CellValue) =>
        t(`suppliers.riskAssessment.status.${value || 'NEW'}`),
      label: t('plots.table.status'),
      sort: true,
    },
  ];

  return (
    <>
      <NewTable<Plot>
        header={{
          action: <AddPlots />,
          title: t<string>('plots.title'),
        }}
        data={plots ?? []}
        columns={columns}
        loading={isLoading}
        empty={{
          entity: t('plots.title').toLowerCase(),
          icon: 'user-plus',
        }}
        cardRows={PLOT_CARD_ROWS}
        onSelect={showPlot}
        virtualized
        search={{
          keys: ['name', 'commodities', 'suppliers.name'],
          placeholder: t<string>('map.plots.searchPlots'),
        }}
        filters={filters}
        selectKey={SEARCH_PARAMS_KEYS.PLOT_ID}
        aside={<PlotsSidePanel plots={plots ?? []} />}
        renderCardBorderRight={renderPlotCardStatus}
      />
      <PlotsImportModal />
    </>
  );
};

export default NewPlotsTablePanel;
